/**
 * Security API Service for AmalQ
 * Handles all security-related API operations including password changes,
 * 2FA management, and account activity
 */

import { apiClient } from '../../../lib/api';

// Response Types
export interface SecurityApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message: string;
  timestamp: string;
}

// Password Change
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// Activity History
export interface ActivityHistoryRequest {
  limit?: number;
  offset?: number;
  eventType?: string;
  startDate?: string;
  endDate?: string;
}

export interface ActivityItem {
  id: string;
  eventType: string;
  description: string;
  ipAddress: string;
  userAgent: string | null;
  location: {
    country: string;
    region: string;
    city: string;
  } | null;
  deviceInfo: {
    os: string;
    device: string;
    browser: string;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    osVersion: string;
    deviceType: string;
    browserVersion: string;
  } | null;
  success: boolean;
  failureReason?: string | null;
  metadata: unknown | null;
  createdAt: string;
}

export interface ActivitySummary {
  totalEvents: number;
  successfulEvents: number;
  failedEvents: number;
  recentEvents: Array<{
    id: string;
    eventType: string;
    createdAt: string;
    success: boolean;
    ipAddress: string;
  }>;
  suspiciousActivity: boolean;
}

export interface ActivityHistoryResponse {
  summary: ActivitySummary;
  activity: ActivityItem[];
  pagination: {
    limit: number;
    offset: number;
  };
}

// 2FA Status
export interface TwoFactorStatusResponse {
  enabled: boolean;
  method: 'EMAIL' | 'SMS' | null;
  lastUsed?: string;
  backupCodesGenerated: boolean;
  backupCodesRemaining: number;
}

// 2FA Enable
export interface EnableTwoFactorRequest {
  method: 'email' | 'sms';
}

export interface EnableTwoFactorResponse {
  expiresAt: string;
}

// 2FA Verify
export interface VerifyTwoFactorRequest {
  code: string;
  rememberDevice?: boolean; // Phase 2: Remember this device for 30 days
}

export interface VerifyTwoFactorResponse {
  success: boolean;
  message: string;
}

// 2FA Disable
export interface DisableTwoFactorRequest {
  password: string;
}

export interface DisableTwoFactorResponse {
  success: boolean;
  message: string;
}

// Trusted Devices Management (Phase 2)
export interface TrustedDevice {
  id: string;
  fingerprint: string;
  userAgent: string;
  ipAddress: string;
  lastUsed: string;
  trustedAt: string;
  expiresAt: string;
  deviceInfo?: {
    os: string;
    browser: string;
    device: string;
    location?: string;
  };
}

export interface TrustedDevicesResponse {
  success: boolean;
  data: TrustedDevice[];
  message: string;
  timestamp: string;
}

export interface RevokeDeviceTrustRequest {
  deviceFingerprint: string;
}

export interface RevokeDeviceTrustResponse {
  success: boolean;
  message: string;
  timestamp: string;
}

// Backup Codes
export interface GenerateBackupCodesResponse {
  codes: string[];
  count: number;
}

// Error Types
export interface SecurityApiError {
  success: false;
  message: string;
  timestamp: string;
}

// Rate Limit Headers
export interface RateLimitHeaders {
  'X-RateLimit-Limit'?: string;
  'X-RateLimit-Remaining'?: string;
  'X-RateLimit-Reset'?: string;
  'Retry-After'?: string;
}

/**
 * Security API Service
 * All methods include proper error handling and rate limit detection
 */
export const securityApi = {
  /**
   * Change user password
   * PUT /api/v1/auth/change-password
   */
  async changePassword(request: ChangePasswordRequest): Promise<SecurityApiResponse<ChangePasswordResponse>> {
    try {
      const response = await apiClient.put<ChangePasswordResponse>('/auth/change-password', request);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Password changed successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to change password');
    } catch (error: unknown) {
      console.error('Security API Error in changePassword:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Get account activity history
   * GET /api/v1/auth/activity
   */
  async getActivityHistory(params?: ActivityHistoryRequest): Promise<ActivityHistoryResponse> {
    try {
      console.log('🔧 Security API: getActivityHistory called with params:', params);
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      if (params?.eventType) queryParams.append('eventType', params.eventType);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const queryString = queryParams.toString();
      const endpoint = queryString ? `/auth/activity?${queryString}` : '/auth/activity';

      const response = await apiClient.get<ActivityHistoryResponse>(endpoint);
      console.log('🔧 Security API: Raw response from apiClient:', response);

      if (response.success && response.data) {
        console.log('🔧 Security API: Returning unwrapped data:', response.data);
        // The response.data contains the actual API response, we need to extract the data field
        return (response.data as unknown as { data: ActivityHistoryResponse }).data;
      }

      throw new Error(response.message || 'Failed to fetch activity history');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch activity history';
      throw new Error(errorMessage);
    }
  },

  /**
   * Get 2FA status
   * GET /api/v1/auth/2fa/status
   */
  async getTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
    try {
      console.log('🔧 Security API: getTwoFactorStatus called');
      const response = await apiClient.get<TwoFactorStatusResponse>('/auth/2fa/status');
      console.log('🔧 Security API: Raw response from apiClient:', response);

      if (response.success && response.data) {
        console.log('🔧 Security API: Returning unwrapped data:', response.data);
        // The response.data contains the actual API response, we need to extract the data field
        return (response.data as unknown as { data: TwoFactorStatusResponse }).data;
      }

      throw new Error(response.message || 'Failed to fetch 2FA status');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch 2FA status';
      throw new Error(errorMessage);
    }
  },

  /**
   * Enable 2FA
   * POST /api/v1/auth/2fa/enable
   */
  async enableTwoFactor(request: EnableTwoFactorRequest): Promise<SecurityApiResponse<EnableTwoFactorResponse>> {
    try {
      const response = await apiClient.post<EnableTwoFactorResponse>('/auth/2fa/enable', request);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Verification code sent successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to enable 2FA');
    } catch (error: unknown) {
      console.error('Security API Error in enableTwoFactor:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to enable 2FA';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Verify 2FA setup code
   * POST /api/v1/auth/2fa/verify
   */
  async verifyTwoFactor(request: VerifyTwoFactorRequest): Promise<SecurityApiResponse<VerifyTwoFactorResponse>> {
    try {
      const response = await apiClient.post<VerifyTwoFactorResponse>('/auth/2fa/verify', request);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || '2FA verification successful',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to verify 2FA code');
    } catch (error: unknown) {
      console.error('Security API Error in verifyTwoFactor:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify 2FA code';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Disable 2FA
   * POST /api/v1/auth/2fa/disable
   */
  async disableTwoFactor(request: DisableTwoFactorRequest): Promise<SecurityApiResponse<DisableTwoFactorResponse>> {
    try {
      const response = await apiClient.post<DisableTwoFactorResponse>('/auth/2fa/disable', request);

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || '2FA disabled successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to disable 2FA');
    } catch (error: unknown) {
      console.error('Security API Error in disableTwoFactor:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to disable 2FA';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Generate backup codes
   * POST /api/v1/auth/2fa/generate-backup-codes
   */
  async generateBackupCodes(): Promise<SecurityApiResponse<GenerateBackupCodesResponse>> {
    try {
      const response = await apiClient.post<GenerateBackupCodesResponse>('/auth/2fa/generate-backup-codes');

      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Backup codes generated successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to generate backup codes');
    } catch (error: unknown) {
      console.error('Security API Error in generateBackupCodes:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate backup codes';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Resend 2FA verification code
   * POST /api/v1/auth/2fa/resend
   */
  async resendTwoFactorCode(): Promise<SecurityApiResponse> {
    try {
      const response = await apiClient.post('/auth/2fa/resend');

      if (response.success) {
        return {
          success: true,
          message: response.message || 'Verification code resent successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to resend verification code');
    } catch (error: unknown) {
      console.error('Security API Error in resendTwoFactorCode:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend verification code';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Get Trusted Devices
   * GET /api/v1/auth/trusted-devices
   */
  async getTrustedDevices(): Promise<SecurityApiResponse<TrustedDevice[]>> {
    try {
      console.log('🔐 Security API: getTrustedDevices called');
      const response = await apiClient.get<TrustedDevicesResponse>('/auth/trusted-devices');
      console.log('🔐 Security API: Raw response from apiClient:', response);

      if (response.success && response.data) {
        console.log('🔐 Security API: Returning unwrapped data:', response.data);
        const devices = (response.data as unknown as { data: TrustedDevice[] }).data;
        return {
          success: true,
          data: devices,
          message: response.message || 'Trusted devices retrieved successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to fetch trusted devices');
    } catch (error: unknown) {
      console.error('🔐 Security API Error in getTrustedDevices:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch trusted devices';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },

  /**
   * Revoke Device Trust
   * DELETE /api/v1/auth/trusted-devices/{deviceFingerprint}
   */
  async revokeDeviceTrust(request: RevokeDeviceTrustRequest): Promise<SecurityApiResponse> {
    try {
      console.log('🔐 Security API: revokeDeviceTrust called with:', request);
      const response = await apiClient.delete<RevokeDeviceTrustResponse>(`/auth/trusted-devices/${request.deviceFingerprint}`);
      console.log('🔐 Security API: Raw response from apiClient:', response);

      if (response.success) {
        return {
          success: true,
          message: response.message || 'Device trust revoked successfully',
          timestamp: response.timestamp || new Date().toISOString(),
        };
      }

      throw new Error(response.message || 'Failed to revoke device trust');
    } catch (error: unknown) {
      console.error('🔐 Security API Error in revokeDeviceTrust:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to revoke device trust';
      throw {
        success: false,
        message: errorMessage,
        timestamp: new Date().toISOString(),
      } as SecurityApiError;
    }
  },
};
